import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star, Disc3 } from "lucide-react";
import { adminApi, mediaUrl } from "../api";
import { AField, Btn, Modal, PageHead, StatusPill, Toggle, Uploader, inputCls, fmtDate } from "../components";

const EMPTY = { title: "", artist: "", description: "", category: "", artwork: "", file: "", featured: false, status: "draft" };

export default function Music() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/tracks").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    setForm({ title: t.title, artist: t.artist || "", description: t.description || "", category: t.category || "", artwork: t.artwork || "", file: t.file || "", featured: !!t.featured, status: t.status });
    setEditing(t.id);
  };

  const save = async (status) => {
    setSaving(true);
    try {
      const payload = { ...form, status };
      if (editing === "new") await adminApi.post("/admin/tracks", payload);
      else await adminApi.put(`/admin/tracks/${editing}`, payload);
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (t, status) => {
    await adminApi.post(`/admin/tracks/${t.id}/status`, { status });
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}" and its files?`)) return;
    await adminApi.delete(`/admin/tracks/${t.id}`);
    load();
  };

  return (
    <div data-testid="admin-music">
      <PageHead title="MUSIC LIBRARY" sub="Upload MP4s, add artwork, publish to the public player.">
        <Btn onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="music-new">
          <Plus size={13} /> UPLOAD MUSIC
        </Btn>
      </PageHead>

      <div className="border border-woodborder" data-testid="music-list">
        {items.length === 0 && (
          <p className="p-8 font-mono text-xs text-clay text-center" data-testid="music-empty">NO TRACKS YET — UPLOAD THE FIRST RELEASE.</p>
        )}
        {items.map((t, i) => (
          <div key={t.id} className="flex items-center gap-4 px-4 md:px-6 py-4 border-b border-woodborder last:border-b-0 hover:bg-card/30 transition-colors" data-testid={`track-row-${i}`}>
            {t.artwork ? (
              <img src={mediaUrl(t.artwork)} alt="" className="w-12 h-12 object-cover border border-woodborder shrink-0" />
            ) : (
              <div className="w-12 h-12 border border-woodborder bg-wood flex items-center justify-center shrink-0"><Disc3 size={18} className="text-clay" /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-editorial font-bold text-cream text-sm truncate">{t.title}</p>
                {t.featured && <Star size={12} className="text-gold fill-gold shrink-0" />}
              </div>
              <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-clay">
                {(t.artist || "UNKNOWN ARTIST").toUpperCase()} — {fmtDate(t.created_at)} {t.file ? "— MP4 ATTACHED" : "— NO FILE"}
              </p>
            </div>
            <StatusPill status={t.status} />
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => openEdit(t)} className="p-2 text-clay hover:text-cream" title="Edit" data-testid={`track-edit-${i}`}><Pencil size={16} /></button>
              <Btn variant={t.status === "published" ? "ghost" : "gold"} className="!px-3 !py-1.5 hidden md:inline-flex" onClick={() => setStatus(t, t.status === "published" ? "draft" : "published")} data-testid={`track-toggle-${i}`}>
                {t.status === "published" ? "UNPUBLISH" : "PUBLISH"}
              </Btn>
              <button onClick={() => remove(t)} className="p-2 text-clay hover:text-red-400" title="Delete" data-testid={`track-delete-${i}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "UPLOAD MUSIC" : "EDIT TRACK"} wide>
        <div className="space-y-5" data-testid="track-editor">
          <AField label="MP4 FILE *">
            <Uploader onUploaded={(p) => setForm({ ...form, file: p })} accept=".mp4,video/mp4,audio/mp4,audio/mpeg,audio/x-m4a" label="SELECT MP4 FROM COMPUTER" testId="track-file-upload" />
            {form.file && (
              <div className="mt-3 border border-woodborder bg-obsidian/50 p-3">
                <p className="font-mono text-[9px] tracking-[0.2em] text-tan mb-2">PREVIEW</p>
                <audio controls src={mediaUrl(form.file)} className="w-full" data-testid="track-preview-player" />
              </div>
            )}
          </AField>
          <div className="grid sm:grid-cols-2 gap-5">
            <AField label="TITLE *"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="track-title" /></AField>
            <AField label="ARTIST"><input className={inputCls} value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} data-testid="track-artist" /></AField>
          </div>
          <AField label="COVER ARTWORK">
            <div className="flex items-start gap-4 flex-wrap">
              <Uploader onUploaded={(p) => setForm({ ...form, artwork: p })} accept="image/*" label="UPLOAD ARTWORK" testId="track-artwork-upload" />
              {form.artwork && <img src={mediaUrl(form.artwork)} alt="Artwork" className="w-24 h-24 object-cover border border-woodborder" data-testid="track-artwork-preview" />}
            </div>
          </AField>
          <AField label="DESCRIPTION"><textarea className={`${inputCls} min-h-[90px] resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="track-description" /></AField>
          <div className="grid sm:grid-cols-2 gap-5">
            <AField label="CATEGORY (OPTIONAL)"><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. PREMIERE" data-testid="track-category" /></AField>
            <div className="flex items-end pb-1"><Toggle checked={form.featured} onChange={(v) => setForm({ ...form, featured: v })} label="FEATURED RELEASE" testId="track-featured" /></div>
          </div>
          <div className="flex flex-wrap gap-3 pt-2 border-t border-woodborder">
            <Btn variant="ghost" disabled={saving} onClick={() => save("draft")} data-testid="track-save-draft">{saving ? "SAVING..." : "SAVE DRAFT"}</Btn>
            <Btn variant="gold" disabled={saving || !form.title.trim() || !form.file} onClick={() => save("published")} data-testid="track-publish">{saving ? "SAVING..." : "PUBLISH"}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
